import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import tokenRequest from "owa-service/lib/factory/tokenRequest";
import getAccessTokenforResourceOperation from "owa-service/lib/operation/getAccessTokenforResourceOperation";
import type RequestOptions from "owa-service/lib/RequestOptions";
import type { TokenRequestParams } from "owa-tokenprovider-utils";
import { getUserTokenIfAvailable } from "./getUserTokenIfAvailable";
import { isNonTransientError } from "./isNonTransientError";
import sleep from "owa-sleep";
import { buildAuthorizationHeader } from "owa-tokenprovider-utils";
import { getOriginWithTrailingSlash } from "owa-url/lib/getOrigin";
import getMailboxRequestOptions from "owa-service/lib/getMailboxRequestOptions";
import { shouldFetchFromSTS } from "./shouldFetchFromSTS";
import { isSuccessStatusCode } from "owa-http-status-codes";
import type TokenResponseCode from "owa-service/lib/contract/TokenResponseCode";
import { isOriginUrl } from "./isOriginUrl";
import { recordRetryAttempt, recordRetryFailure } from "./recordRetryTelemetry";
import type { TokenPerformanceLogger } from "./TokenPerformanceLogger";

const TELEMETRY_PREFIX = "FetchFromServer";

export async function fetchTokenFromServerWithRetry(perfLog: TokenPerformanceLogger, retryCount: number, retryDelay: number, requestParams: TokenRequestParams, attemptIndex: number = 1, backoffAccumulatedMs: number = 0): Promise<TokenResponse> {
    if (retryCount < 1) {
        const error = new Error("Max retrying limit (1) reached");
        recordRetryFailure(perfLog, TELEMETRY_PREFIX, attemptIndex - 1, backoffAccumulatedMs);
        return Promise.reject(error);
    }
    
    return fetchTokenFromServer(perfLog, requestParams).then(result => {
        recordRetryAttempt(perfLog, TELEMETRY_PREFIX, attemptIndex, backoffAccumulatedMs);
        return result;
    }, error => {
        perfLog.addCustomProperty("HttpStatusCode", error?.httpStatus);
        perfLog.addCustomProperty("cV", error?.correlationVector);
        perfLog.addCustomProperty("ServerCorrelationId", error?.correlationId);
        
        // Retry only if attempts remain. On the final attempt, reject with the real error.
        if (isNonTransientError(error) && retryCount > 1) {
            const sleepStart = Date.now();
            return sleep(retryDelay).then(() => fetchTokenFromServerWithRetry(perfLog, retryCount - 1, retryDelay * 2, { ...requestParams }, attemptIndex + 1, backoffAccumulatedMs + Math.max(0, Date.now() - sleepStart)));
        }
        
        recordRetryFailure(perfLog, TELEMETRY_PREFIX, attemptIndex, backoffAccumulatedMs);
        return Promise.reject(error);
    });
}

export async function fetchTokenFromServer(perfLog: TokenPerformanceLogger, params: TokenRequestParams): Promise<TokenResponse> {
    const resource = params.resource ?? getOriginWithTrailingSlash();
    const authTokenResponse = await getUserTokenIfAvailable(params);
    
    if (authTokenResponse?.AccessToken && isOriginUrl(resource)) {
        perfLog.addCheckpoint("UserTokenFetched");
        return authTokenResponse;
    }
    
    const fetchSTSToken = shouldFetchFromSTS(resource);
    const setUserTokenPayload = !fetchSTSToken;
    
    if (fetchSTSToken) {
        perfLog.addCheckpoint("FetchingSTSToken");
    }
    
    const options: RequestOptions = {
        isUserActivity: false,
        returnFullResponseOnSuccess: true,
        mailboxInfo: params.mailboxInfo,
    };
    
    if (authTokenResponse?.AccessToken) {
        options.headers = new Headers();
        options.headers.set("authorization", buildAuthorizationHeader(authTokenResponse?.AccessToken));
    }
    
    const tokenRequestParams = tokenRequest({
        Resource: params.resource,
        TargetTenantId: params.targetTenantId,
        PreferIdpToken: params.preferIdpToken,
        SendClientCapabilityClaim: params.sendClientCapabilityClaim,
    });
    
    if (authTokenResponse?.AccessToken && setUserTokenPayload) {
        tokenRequestParams.UserToken = authTokenResponse?.AccessToken;
        perfLog.addCheckpoint("UserTokenAttached");
    }
    
    const response = await (getAccessTokenforResourceOperation(tokenRequestParams, getMailboxRequestOptions(params.mailboxInfo, options)) as Promise<Response>);
    
    if (response && isSuccessStatusCode(response.status)) {
        perfLog.addCustomProperty("ServerTokenResult", response?.headers?.get("x-tokenresult") || "");
        perfLog.addCustomProperty("ServerRequestId", response?.headers?.get("request-id") || "");
    }
    
    // If .json is not present it will mean that one of the underlying layers for handling the request
    // dropped the returnFullResponseOnSuccess setting and return TokenResponse directly.
    const tokenResponse = (await response.json()) as TokenResponse;
    perfLog.addCustomProperty("ServerTokenStatus", tokenResponse?.TokenResultCode?.toString() || 2..toString());
    
    if (!!tokenResponse?.SubErrorCode) {
        perfLog.addCustomProperty("IdpSubErrorCode", tokenResponse?.SubErrorCode ?? "");
    }
    
    return tokenResponse;
}
